/**
 * Brand Shapes — The Medical Avenue
 * Four symbolic elements from the brand identity:
 * Star · Sun · Swan · Lotus
 */

interface ShapeProps {
  className?: string;
  size?: number;
  color?: string;
}

/** ✦ Star — 4-point star, embodies doctors and medical expertise */
export function StarShape({ className = "", size = 48, color = "currentColor" }: ShapeProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="34 34 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
    >
      <path
        d="M 50.0 35.16 L 49.76 37.84 L 48.87 40.84 L 47.71 43.07 L 45.8 45.49 L 43.67 47.31 L 40.84 48.87 L 37.84 49.76 L 35.16 50.0 L 37.84 50.22 L 40.84 51.11 L 43.07 52.27 L 45.49 54.18 L 47.31 56.31 L 48.87 59.13 L 49.76 62.13 L 50.0 64.82 L 50.22 62.13 L 51.11 59.13 L 52.27 56.91 L 54.18 54.49 L 56.31 52.67 L 59.13 51.11 L 62.13 50.22 L 64.82 50.0 L 62.13 49.76 L 59.13 48.87 L 56.31 47.31 L 54.18 45.49 L 52.27 43.07 L 51.11 40.84 L 50.22 37.84 Z"
        fill={color}
        fillRule="evenodd"
      />
    </svg>
  );
}

/** ☀ Sun — half-risen sun with rays, radiates vitality */
export function SunShape({ className = "", size = 48, color = "currentColor" }: ShapeProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="37 37 26 26"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
    >
      <path
        d="M 49.36 38.09 L 47.29 38.4 L 48.22 43.07 L 47.84 43.64 L 47.13 43.27 L 46.22 38.69 L 43.78 39.82 L 41.76 41.38 L 43.87 44.6 L 43.64 45.24 L 42.93 45.16 L 40.96 42.22 L 39.51 44.31 L 38.6 46.49 L 40.31 47.67 L 40.51 48.31 L 39.89 48.67 L 38.31 47.67 L 38.09 50.67 L 38.36 52.6 L 39.18 55.02 L 40.51 57.22 L 42.16 58.98 L 44.02 60.31 L 45.87 61.18 L 48.22 61.78 L 50.67 61.89 L 53.13 61.49 L 55.07 60.78 L 57.04 59.6 L 58.8 58.02 L 60.11 56.29 L 61.18 54.11 L 61.78 51.76 L 61.89 49.33 L 61.67 47.67 L 60.09 48.67 L 59.47 48.31 L 59.67 47.67 L 61.38 46.49 L 60.47 44.31 L 59.02 42.22 L 57.0 45.2 L 56.51 45.33 L 56.11 44.98 L 56.16 44.51 L 58.22 41.42 L 55.98 39.69 L 53.76 38.69 L 52.84 43.27 L 52.13 43.64 L 51.76 43.07 L 52.69 38.4 Z"
        fill={color}
        fillRule="evenodd"
      />
    </svg>
  );
}

/** 𝕊 Swan — elegant S-curve swan, symbolizes balance */
export function SwanShape({ className = "", size = 48, color = "currentColor" }: ShapeProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="37 37 26 26"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
    >
      <path
        d="M 39.62 44.51 L 39.33 46.36 L 39.49 48.78 L 40.2 51.36 L 41.22 53.36 L 42.53 55.0 L 44.22 56.38 L 46.16 57.24 L 48.38 57.62 L 48.64 58.11 L 48.31 58.42 L 47.6 58.38 L 44.96 57.67 L 43.31 56.73 L 41.62 55.2 L 40.36 53.49 L 39.38 51.47 L 38.76 49.29 L 38.51 46.93 L 38.11 49.8 L 38.38 52.51 L 39.27 55.11 L 40.87 57.6 L 42.24 58.67 L 43.44 59.31 L 46.58 60.24 L 49.44 60.24 L 50.71 59.87 L 51.71 59.27 L 52.56 58.29 L 53.02 56.73 L 52.87 55.36 L 52.04 53.8 L 50.13 52.29 L 45.38 50.44 L 42.6 48.82 L 40.73 46.89 Z"
        fill={color}
        fillRule="evenodd"
      />
      <path
        d="M 49.8 38.11 L 46.91 38.51 L 43.91 39.78 L 41.47 41.71 L 40.33 43.07 L 41.04 45.87 L 42.67 47.84 L 45.58 49.64 L 50.71 51.69 L 52.07 52.67 L 53.24 54.16 L 53.78 55.69 L 53.78 57.24 L 53.2 58.78 L 52.13 59.96 L 50.67 60.76 L 48.96 61.13 L 44.8 60.67 L 47.27 61.56 L 50.18 61.87 L 52.04 61.53 L 54.18 59.93 L 55.18 57.82 L 55.22 55.73 L 54.71 54.13 L 53.67 52.56 L 48.64 48.51 L 46.84 46.4 L 46.2 43.82 L 47.13 41.64 L 48.58 40.6 L 50.51 40.51 L 51.71 41.2 L 52.87 42.76 L 53.8 43.38 L 53.51 43.91 L 52.89 43.78 L 50.93 41.6 L 49.42 41.22 L 47.69 42.22 L 47.0 44.29 L 47.36 45.67 L 48.29 47.02 L 53.6 51.33 L 55.58 54.09 L 56.07 55.89 L 56.02 57.73 L 55.33 59.67 L 54.2 61.09 L 57.36 59.31 L 59.71 56.82 L 61.4 53.31 L 61.87 49.8 L 61.6 47.47 L 60.87 45.2 L 59.71 43.16 L 58.18 41.38 L 56.29 39.91 L 54.18 38.87 Z"
        fill={color}
        fillRule="evenodd"
      />
    </svg>
  );
}

/** 🪷 Lotus — multi-petal lotus, conveys beauty and renewal */
export function LotusShape({ className = "", size = 48, color = "currentColor" }: ShapeProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="37 37 26 26"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
    >
      <path d="M 50.4 61.82 L 51.49 61.76 L 52.91 61.49 L 54.24 61.07 L 55.6 60.44 L 56.82 59.69 L 57.6 59.09 L 58.4 58.36 L 59.36 57.24 L 58.64 57.49 L 57.64 57.71 L 56.82 57.8 L 55.91 57.8 L 55.24 57.73 L 54.27 57.53 L 53.44 57.27 L 52.69 56.93 L 52.2 57.36 L 51.8 57.84 L 51.38 58.49 L 51.04 59.13 L 50.84 59.62 L 50.56 60.6 L 50.44 61.2 Z" fill={color} fillRule="evenodd" />
      <path d="M 49.58 61.82 L 49.42 60.6 L 49.13 59.62 L 48.91 59.09 L 48.53 58.38 L 48.11 57.76 L 47.33 56.91 L 46.53 57.27 L 45.71 57.53 L 44.73 57.73 L 44.07 57.8 L 43.16 57.8 L 42.33 57.71 L 41.58 57.56 L 40.62 57.24 L 41.56 58.33 L 42.38 59.09 L 43.16 59.69 L 44.38 60.44 L 45.73 61.07 L 47.07 61.49 L 48.49 61.76 Z" fill={color} fillRule="evenodd" />
      <path d="M 49.31 55.51 L 48.69 56.07 L 48.02 56.49 L 48.0 56.58 L 48.69 57.33 L 49.29 58.24 L 49.73 59.16 L 49.89 59.56 L 50.0 59.96 L 50.07 59.62 L 50.29 59.04 L 50.69 58.24 L 51.29 57.33 L 51.98 56.58 L 51.29 56.07 L 50.67 55.51 Z" fill={color} fillRule="evenodd" />
      <path d="M 49.96 54.8 L 50.02 54.8 Z" fill={color} fillRule="evenodd" />
      <path d="M 60.6 55.29 L 59.84 55.02 L 58.84 54.82 L 58.4 54.78 L 57.38 54.78 L 56.76 54.84 L 55.76 55.07 L 54.84 55.4 L 53.84 55.93 L 53.22 56.4 L 54.22 56.78 L 55.38 57.02 L 55.84 57.07 L 56.87 57.07 L 57.47 57.0 L 58.31 56.82 L 58.93 56.62 L 60.0 56.13 L 60.22 55.98 Z" fill={color} fillRule="evenodd" />
      <path d="M 39.38 55.29 L 39.76 55.98 L 39.93 56.11 L 40.6 56.44 L 41.04 56.62 L 41.67 56.82 L 42.51 57.0 L 43.11 57.07 L 44.13 57.07 L 44.76 57.0 L 45.33 56.89 L 46.04 56.69 L 46.62 56.47 L 46.76 56.38 L 46.13 55.93 L 45.13 55.4 L 44.22 55.07 L 43.36 54.87 L 42.6 54.78 L 41.33 54.8 L 40.4 54.96 L 39.98 55.07 Z" fill={color} fillRule="evenodd" />
      <path d="M 57.42 48.6 L 56.87 48.76 L 56.31 48.98 L 55.36 49.49 L 54.51 50.11 L 53.73 50.87 L 53.07 51.73 L 52.49 52.78 L 52.18 53.58 L 52.02 54.13 L 51.96 54.56 L 52.89 54.24 L 53.89 53.73 L 54.71 53.16 L 55.62 52.29 L 56.33 51.36 L 56.89 50.33 L 57.22 49.44 Z" fill={color} fillRule="evenodd" />
      <path d="M 42.56 48.6 L 42.76 49.44 L 43.09 50.33 L 43.64 51.36 L 44.36 52.29 L 45.13 53.04 L 46.09 53.73 L 47.09 54.24 L 48.04 54.56 L 47.84 53.69 L 47.49 52.76 L 47.16 52.11 L 46.8 51.56 L 46.13 50.73 L 45.4 50.04 L 44.49 49.4 L 43.58 48.93 L 42.78 48.64 Z" fill={color} fillRule="evenodd" />
      <path d="M 50.0 45.49 L 49.53 46.29 L 49.18 47.18 L 48.91 48.27 L 48.82 49.04 L 48.84 50.29 L 49.07 51.51 L 49.42 52.53 L 49.67 53.04 L 50.0 53.58 L 50.44 52.78 L 50.8 51.89 L 51.07 50.8 L 51.16 50.02 L 51.13 48.78 L 50.91 47.56 L 50.56 46.53 Z" fill={color} fillRule="evenodd" />
      <path d="M 39.0 45.51 L 38.31 47.89 L 38.13 50.44 L 39.07 54.62 L 40.96 54.11 L 42.82 54.07 L 45.13 54.62 L 47.47 56.0 L 48.38 55.38 L 45.76 54.38 L 43.8 52.76 L 42.36 50.47 L 41.73 47.67 L 44.2 48.42 L 46.24 49.82 L 47.89 51.93 L 48.78 54.71 L 49.04 54.76 L 49.53 54.16 L 48.38 51.71 L 48.11 48.82 L 48.73 46.31 L 50.0 44.27 L 51.24 46.31 L 51.87 48.8 L 51.6 51.71 L 50.44 54.16 L 50.93 54.76 L 51.2 54.71 L 52.2 51.76 L 53.87 49.71 L 55.98 48.33 L 58.24 47.76 L 57.76 50.13 L 56.31 52.6 L 54.4 54.27 L 51.71 55.49 L 52.56 56.02 L 54.53 54.76 L 56.93 54.09 L 58.64 54.07 L 60.96 54.53 L 61.84 50.44 L 61.67 47.89 L 60.98 45.51 L 58.76 42.0 L 56.84 40.31 L 54.56 39.04 L 50.69 38.16 L 47.93 38.31 L 45.42 39.04 L 43.13 40.31 L 41.22 42.0 Z" fill={color} fillRule="evenodd" />
    </svg>
  );
}

/** Circular badge wrapper — matches the brand identity image style */
export function ShapeBadge({
  children,
  size = 64,
  className = "",
}: {
  children: React.ReactNode;
  size?: number;
  className?: string;
}) {
  return (
    <div
      className={`flex items-center justify-center rounded-full bg-brand-forest-700 ${className}`}
      style={{ width: size, height: size }}
    >
      {children}
    </div>
  );
}
